/**
 * HORAIRE DES COURS — public /horaire (univers DARK premium KAIZEN)
 * Filtres : jour, âge, discipline, programme, instructeur.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Clock, ArrowLeft, FunnelSimple, User, CalendarBlank } from "@phosphor-icons/react";
import PublicContactSection from "../components/PublicContactSection";
import DisciplineEmblem from "../components/DisciplineEmblem";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_WHITE = "/logos/master_text_sm.webp";

const DISCIPLINE_COLORS = {
  "Jiu-jitsu Can Ryu": "#D90429",
  "Muay Thai / Kick-Boxing": "#F59E0B",
  "Jiu-jitsu brésilien": "#3B82F6",
  "Conditionnement / Kick-Boxing": "#10B981",
};

function fmtDateFr(iso) {
  const [y, m, d] = (iso || "").split("-").map(Number);
  const mois = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
  return y ? `${d === 1 ? "1er" : d} ${mois[m - 1]} ${y}` : iso;
}

function ageLabel(min, max) {
  if (min == null && max == null) return "Tous âges";
  if (min != null && (max == null || max >= 99)) return `${min} ans et +`;
  if (min == null) return `Jusqu'à ${max} ans`;
  return `${min} à ${max} ans`;
}

export default function HorairePublic() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ jour: "", discipline: "", programme: "", instructeur: "", age: "" });

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v != null) params.set(k, v); });
    const r = await axios.get(`${API}/schedule/public?${params.toString()}`);
    setData(r.data);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const reset = () => setFilters({ jour: "", discipline: "", programme: "", instructeur: "", age: "" });
  const f = data?.filters;

  return (
    <div className="kz-body min-h-screen bg-[#0A0A0A] text-white" data-testid="horaire-page">
      <header className="border-b border-white/10 px-5 md:px-12 py-7 flex items-center justify-between">
        <img src={LOGO_WHITE} alt="Institut Impact Élite Martial" className="h-12 object-contain" data-testid="public-master-logo" />
        <Link to="/univers" className="kz-mono text-[10px] tracking-[0.2em] uppercase text-white/40 hover:text-white transition-colors flex items-center gap-2" data-testid="back-link">
          <ArrowLeft size={14} /> Retour
        </Link>
      </header>

      <div className="max-w-6xl mx-auto px-5 md:px-12 py-12">
        <p className="kz-mono text-[10px] tracking-[0.26em] uppercase text-[#D90429]">Institut Impact Élite Martial · Val-d'Or</p>
        <h1 className="kz-display text-4xl md:text-6xl font-medium mt-3">Horaire des cours</h1>

        {/* Filtres */}
        <div className="mt-8 border border-white/10 p-5" data-testid="filters">
          <div className="flex items-center gap-2 text-white/40 mb-4"><FunnelSimple size={16} /><span className="kz-mono text-[10px] tracking-[0.2em] uppercase">Filtrer</span></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <Sel label="Jour" value={filters.jour} onChange={(v) => set("jour", v)} options={f?.jours} testid="filter-jour" />
            <Sel label="Discipline" value={filters.discipline} onChange={(v) => set("discipline", v)} options={f?.disciplines} testid="filter-discipline" />
            <Sel label="Programme" value={filters.programme} onChange={(v) => set("programme", v)} options={f?.programmes} testid="filter-programme" />
            <Sel label="Instructeur" value={filters.instructeur} onChange={(v) => set("instructeur", v)} options={f?.instructeurs} testid="filter-instructeur" />
            <label className="block">
              <span className="kz-mono text-[10px] tracking-[0.18em] uppercase text-white/40">Âge</span>
              <input type="number" value={filters.age} onChange={(e) => set("age", e.target.value)} placeholder="ex. 10" data-testid="filter-age"
                className="mt-1.5 w-full bg-black/40 border border-white/15 px-3 py-2 text-white outline-none focus:border-[#D90429]" />
            </label>
          </div>
          {(filters.jour || filters.discipline || filters.programme || filters.instructeur || filters.age) && (
            <button onClick={reset} data-testid="reset-filters" className="mt-3 kz-mono text-[10px] tracking-[0.2em] uppercase text-[#D90429] hover:text-white transition-colors">Réinitialiser</button>
          )}
        </div>

        {/* Résultats */}
        {!data ? (
          <p className="text-white/40 mt-10">Chargement…</p>
        ) : data.days.length === 0 ? (
          <p className="text-white/50 mt-10" data-testid="no-results">Aucun cours pour ces critères.</p>
        ) : (
          <div className="mt-10 space-y-10">
            {data.days.map((d) => (
              <div key={d.jour} data-testid={`day-${d.jour}`}>
                <div className="flex items-center gap-3 mb-4">
                  <CalendarBlank size={20} className="text-[#D90429]" />
                  <h2 className="kz-display text-2xl">{d.jour}</h2>
                  <span className="kz-mono text-[10px] text-white/30">{d.cours.length} cours</span>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {d.cours.map((c) => (
                    <div key={c.id} data-testid={`course-${c.id}`} className="border border-white/10 bg-white/[0.02] p-5 hover:border-white/25 transition-colors"
                      style={{ borderLeft: `3px solid ${DISCIPLINE_COLORS[c.discipline] || "#888"}` }}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-2.5 min-w-0">
                          <DisciplineEmblem name={c.discipline} size={34} testId={`course-emblem-${c.id}`} />
                          <h3 className="kz-display text-lg leading-tight">{c.titre_du_cours}</h3>
                        </div>
                        <span className="kz-mono text-xs text-white/70 whitespace-nowrap flex items-center gap-1"><Clock size={13} />{c.heure_debut}{c.heure_fin ? `–${c.heure_fin}` : ""}</span>
                      </div>
                      {c.changement_prevu && (
                        <div className="mt-2 inline-flex items-center gap-1.5 bg-[#D90429]/15 border border-[#D90429]/50 text-[#F59E0B] px-2 py-1 text-[11px] kz-mono" data-testid={`course-change-${c.id}`}>
                          <Clock size={11} /> Dès le {fmtDateFr(c.changement_prevu.date)} : <b>{c.changement_prevu.heure_debut}{c.changement_prevu.heure_fin ? `–${c.changement_prevu.heure_fin}` : ""}</b>
                        </div>
                      )}
                      {c.ancien_horaire && (
                        <div className="mt-2 text-[11px] text-white/40 kz-mono" data-testid={`course-changed-${c.id}`}>Nouvel horaire depuis le {fmtDateFr(c.ancien_horaire.depuis)} (avant : {c.ancien_horaire.heure_debut})</div>
                      )}
                      <div className="flex flex-wrap gap-1.5 mt-2.5">
                        <Tag>{c.discipline}</Tag>
                        {c.programme && <Tag muted>{c.programme}</Tag>}
                        <Tag muted>{ageLabel(c.age_min, c.age_max)}</Tag>
                        {c.exception_age && <span className="text-[9px] uppercase tracking-wide bg-amber-500/20 text-amber-300 border border-amber-500/40 px-1.5 py-0.5">Exception d'âge</span>}
                      </div>
                      {c.niveau_ceinture && <div className="kz-body text-xs text-white/50 mt-2">Ceinture / niveau : {c.niveau_ceinture}</div>}
                      {c.instructeur_principal && (
                        <div className="kz-body text-xs text-white/60 mt-2 flex items-center gap-1.5"><User size={13} className="text-[#D90429]" />{c.instructeur_principal}{c.assistants?.length ? ` · ${c.assistants.join(", ")}` : ""}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <PublicContactSection source="horaire" title="Une question sur l'horaire ?" subtitle="Cours d'essai, disponibilités, niveaux — écris-nous, on te répond rapidement." />
    </div>
  );
}

function Sel({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="kz-mono text-[10px] tracking-[0.18em] uppercase text-white/40">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1.5 w-full bg-black/40 border border-white/15 px-3 py-2 text-white outline-none focus:border-[#D90429]">
        <option value="">Tous</option>
        {(options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function Tag({ children, muted }) {
  return <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 ${muted ? "bg-white/5 text-white/50" : "bg-[#D90429]/15 text-[#D90429]"}`}>{children}</span>;
}
